import React, {useCallback, useState, useEffect} from 'react';
import {Flex, Button, Typography, Spin, message, Switch} from 'antd';
import {BulbOutlined, RobotFilled, CheckCircleOutlined} from '@ant-design/icons';
import {ModelDTO} from 'hemwb-api';
import {ModelFamilyDisplay} from './ModelFamilyDisplay';
import {useSettings} from '../../../../store/appData/hooks';
import {requestUpdateAiSummaryPreference, requestModelDetail} from '../../../../store/model/api';

interface OverviewTabProps {
  currentModelUuid: string;
  aiSummary: string | undefined;
  enableRequestChange: boolean | undefined;
  familyModels: (ModelDTO & {children?: ModelDTO[]})[] | undefined;
  isExternalUser: boolean;
  /** HEMT-8938: true when model owner opted out of AI summary */
  aiSummaryOptedOut?: boolean;
  /** HEMT-9520: Status of AI summary generation (PENDING/READY/FAILED) */
  aiSummaryStatus?: string;
  /** HEMT-8938: true when current user is the model entry creator/owner */
  isOwner?: boolean;
  /** HEMT-8938: true when the model has a Model Development Plan. Distinguishes
   * 'summary is being generated' from 'no MDP exists' when aiSummary is null. */
  hasMdp?: boolean;
  /** HEMT-9520: true when the model has at least one Technical Report file. */
  hasTechnicalReport?: boolean;
}

/**
 * Overview tab - displays the model family tree
 *
 */
export const OverviewTab: React.FC<OverviewTabProps> = ({
  currentModelUuid,
  aiSummary,
  enableRequestChange,
  familyModels,
  isExternalUser,
  aiSummaryOptedOut = false,
  aiSummaryStatus = undefined,
  isOwner = false,
  hasMdp = false,
  hasTechnicalReport = false,
}) => {
  const settings = useSettings();
  const supportEmail = settings?.supportEmail || 'AMS_HEMWB_SUPPORT@merck.com';

  // HEMT-8938: local state to optimistically reflect turning-on action
  const [localOptedOut, setLocalOptedOut] = useState(aiSummaryOptedOut);
  const [turningOn, setTurningOn] = useState(false);
  const [turnedOnPending, setTurnedOnPending] = useState(false);

  // HEMT-9520: Track current status and summary for polling
  const [currentSummary, setCurrentSummary] = useState<string | undefined>(aiSummary);
  const [currentStatus, setCurrentStatus] = useState<string | undefined>(aiSummaryStatus);
  const hasAnySources = hasMdp || hasTechnicalReport;

  const getPendingSourcePhrase = () => {
    if (hasMdp && hasTechnicalReport) {
      return 'the Model Development Plan and Technical Report';
    }
    if (hasMdp) {
      return 'the Model Development Plan';
    }
    if (hasTechnicalReport) {
      return 'the Technical Report';
    }
    return null;
  };

  // Update local state when props change
  useEffect(() => {
    setCurrentSummary(aiSummary);
    setCurrentStatus(aiSummaryStatus);
  }, [aiSummary, aiSummaryStatus]);

  const handleTurnOnAiSummary = useCallback(async () => {
    setTurningOn(true);
    try {
      await requestUpdateAiSummaryPreference(currentModelUuid, false);
      setLocalOptedOut(false);
      setTurnedOnPending(true);
      message.success('AI summary has been turned on. The summary will appear once generated.');
    } catch {
      message.error('Failed to turn on AI summary. Please try again later.');
    } finally {
      setTurningOn(false);
    }
  }, [currentModelUuid]);

  // HEMT-9520: Poll for AI summary status when PENDING
  useEffect(() => {
    const shouldPollForAiSummary = currentStatus === 'PENDING' || (!currentStatus && !currentSummary && hasAnySources);
    if (!shouldPollForAiSummary) {
      return;
    }

    const pollInterval = 5000; // 5 seconds (configurable via environment)

    const timer = window.setInterval(async () => {
      try {
        const response = await requestModelDetail(currentModelUuid);
        if (response) {
          const newStatus = (response as any)?.aiSummaryStatus;
          const newSummary = (response as any)?.aiSummary;

          setCurrentStatus(newStatus);
          if (newSummary) {
            setCurrentSummary(newSummary);
          }

          if (newStatus === 'READY') {
            message.success('AI summary is ready.');
            setTurnedOnPending(false);
            window.clearInterval(timer);
          } else if (newStatus === 'FAILED') {
            message.error('AI summary generation failed. Please try again later.');
            window.clearInterval(timer);
          }
        }
      } catch (e) {
        // Continue polling on error
      }
    }, pollInterval);

    return () => window.clearInterval(timer);
  }, [currentStatus, currentSummary, hasAnySources, currentModelUuid]);

  // Function to format the AI summary text
  const formatAISummary = (text: string) => {
    // Replace \n with actual line breaks
    let formatted = text.replace(/\\n/g, '\n');

    // Convert **text** to <strong>text</strong>
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Convert <b>text</b> to <strong>text</strong> (keeping HTML bold tags)
    // No need to replace as React will handle HTML tags with dangerouslySetInnerHTML

    return formatted;
  };

  return (
    <div>
      {localOptedOut ? (
        /* HEMT-8938: Opted-out state - Figma design with single toggle */
        <div
          style={{
            padding: '10px',
            marginBottom: '32px',
          }}
        >
          <Flex align="center" justify="space-between" gap={16}>
            {/* Message */}
            <Flex align="center" gap={8}>
              <RobotFilled style={{fontSize: '16px'}} />
              <Typography.Text style={{fontSize: '14px', color: '#262626'}}>
                AI summary is not available for this entry.
              </Typography.Text>
            </Flex>

            {/* Single Toggle - Only show if user is owner */}
            {isOwner && (
              <Flex align="center" gap={8} style={{whiteSpace: 'nowrap'}}>
                <Typography.Text style={{fontSize: '13px', color: '#595959'}}>Turn off</Typography.Text>
                <Switch
                  checked={!localOptedOut}
                  disabled={turningOn}
                  onChange={(checked) => {
                    if (checked) {
                      handleTurnOnAiSummary();
                    }
                  }}
                  size="small"
                />
                <Typography.Text style={{fontSize: '13px', color: '#595959'}}>Turn on</Typography.Text>
              </Flex>
            )}
          </Flex>
        </div>
      ) : turnedOnPending && !aiSummary ? (
        /* HEMT-8938: just turned on — summary pending generation */
        <div
          style={{
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: '4px',
            padding: '16px',
            marginBottom: '32px',
          }}
        >
          <Flex align="center" gap={8}>
            <CheckCircleOutlined style={{fontSize: '16px', color: '#52c41a'}} />
            <Typography.Text style={{fontSize: '13px', color: '#262626'}}>
              AI summary has been enabled. The summary will appear once it has been generated — please reload the page
              shortly.
            </Typography.Text>
          </Flex>
        </div>
      ) : currentStatus === 'PENDING' && !currentSummary ? (
        /* HEMT-9520: Status is PENDING, no existing summary — show spinner only */
        <div
          style={{
            borderRadius: '4px',
            padding: '16px',
            marginBottom: '32px',
          }}
        >
          <Flex align="center" gap={8}>
            <Spin size="small" />
            <Typography.Text style={{fontSize: '13px', color: '#262626'}}>
              {getPendingSourcePhrase()
                ? `AI summary is being generated based on ${getPendingSourcePhrase()}. Please wait, the summary will update automatically when ready.`
                : 'AI summary is being generated. Please wait, the summary will update automatically when ready.'}
            </Typography.Text>
          </Flex>
        </div>
      ) : currentSummary ? (
        <Flex vertical gap={16} style={{marginBottom: '32px'}}>
          {currentStatus === 'PENDING' && (
            <Flex
              align="center"
              gap={8}
              style={{
                padding: '8px 12px',
                backgroundColor: '#fffbe6',
                border: '1px solid #ffe58f',
                borderRadius: '4px',
              }}
            >
              <Spin size="small" />
              <Typography.Text style={{fontSize: '13px', color: '#614700'}}>
                {getPendingSourcePhrase()
                  ? `Sources have changed — regenerating AI summary based on ${getPendingSourcePhrase()}. Summary will update automatically when ready.`
                  : 'Sources have changed — regenerating AI summary. Summary will update automatically when ready.'}
              </Typography.Text>
            </Flex>
          )}
          <div
            style={{
              backgroundColor: '#fafafa',
              border: '1px solid #e8e8e8',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Flex
              align="center"
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #e8e8e8',
                backgroundColor: '#fafafa',
              }}
            >
              <Flex align="center" gap={8} style={{flex: 1}}>
                <RobotFilled style={{fontSize: '16px'}} />
                <Typography.Text style={{fontSize: '13px', color: '#262626', fontWeight: 500}}>
                  AI-Generated Summary
                </Typography.Text>
                {enableRequestChange && (
                  <Button
                    type="link"
                    size="small"
                    style={{padding: '0 8px', height: 'auto', fontSize: '12px', color: '#1890ff', marginLeft: 'auto'}}
                    icon={<BulbOutlined style={{color: '#faad14', fontSize: '13px'}} />}
                  >
                    <a
                      href={`mailto:${supportEmail}?subject=AI%20Summary%20Change%20Request.`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Request change
                    </a>
                  </Button>
                )}
              </Flex>
            </Flex>

            {/* Summary content */}
            <div style={{padding: '16px'}}>
              <Typography.Paragraph
                style={{
                  margin: '0 0 16px 0',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: '#262626',
                  whiteSpace: 'pre-wrap',
                }}
              >
                <div dangerouslySetInnerHTML={{__html: formatAISummary(currentSummary)}} />
              </Typography.Paragraph>

              <Flex gap={8} justify="space-between" align="flex-end">
                <Typography.Text style={{fontSize: '12px', color: '#8c8c8c', fontStyle: 'italic'}}>
                  AI generated content may be incorrect
                </Typography.Text>
              </Flex>
            </div>
          </div>
        </Flex>
      ) : currentStatus === 'READY' ? (
        /* HEMT-9520: Backend confirmed READY with no summary — no sources available */
        <div
          style={{
            borderRadius: '4px',
            padding: '16px',
            marginBottom: '32px',
          }}
        >
          <Flex align="center" gap={8}>
            <RobotFilled style={{fontSize: '16px'}} />
            <Typography.Text style={{fontSize: '13px', color: '#262626'}}>
              AI summary cannot be generated as no Model Development Plan or Technical Report is available for this
              entry.
            </Typography.Text>
          </Flex>
        </div>
      ) : currentStatus === 'FAILED' ? (
        /* HEMT-9520: Generation failed */
        <div
          style={{
            borderRadius: '4px',
            padding: '16px',
            marginBottom: '32px',
          }}
        >
          <Flex align="center" gap={8}>
            <RobotFilled style={{fontSize: '16px'}} />
            <Typography.Text style={{fontSize: '13px', color: '#262626'}}>
              AI summary generation failed. Please reload the page to try again.
            </Typography.Text>
          </Flex>
        </div>
      ) : hasAnySources ? (
        /* Fallback: status not yet set (legacy data) — show spinner based on source availability */
        <div
          style={{
            borderRadius: '4px',
            padding: '16px',
            marginBottom: '32px',
          }}
        >
          <Flex align="center" gap={8}>
            <Spin size="small" />
            <Typography.Text style={{fontSize: '13px', color: '#262626'}}>
              {getPendingSourcePhrase()
                ? `AI summary is being generated based on ${getPendingSourcePhrase()}. Please wait, the summary will update automatically when ready.`
                : 'AI summary is being generated. Please wait, the summary will update automatically when ready.'}
            </Typography.Text>
          </Flex>
        </div>
      ) : (
        <div
          style={{
            borderRadius: '4px',
            padding: '16px',
            marginBottom: '32px',
          }}
        >
          <Flex align="center" gap={8}>
            <RobotFilled style={{fontSize: '16px'}} />
            <Typography.Text style={{fontSize: '13px', color: '#262626'}}>
              AI summary cannot be generated as no Model Development Plan or Technical Report is available for this
              entry.
            </Typography.Text>
          </Flex>
        </div>
      )}
      <ModelFamilyDisplay
        models={familyModels}
        currentModelUuid={currentModelUuid}
        loading={!familyModels}
        isExternalUser={isExternalUser}
      />
    </div>
  );
};
